import axios from 'axios'
import { computed, ComputedRef, reactive } from 'vue'

import { HttpRouter } from "@Modules/shared/interfaces/HttpRouter";

import GeneralStats from '../DTOs/GeneralStats'
import { FetchForAliasArgs, FetchForTagArgs, FetchForTeamArgs } from './types'

/**
 * Represents the resonse from the server. Eventually this type will be auto generated
 * by the backend but for now its here
 */
export type GeneralStatsResponseItem = {
  name: string | number
  value: number
  // We allow one level of children, child items cannot have children
  children?: { name: string | number; value: number }[]
}
export type GeneralStatsResponse = {
  dataset: {
    browser: GeneralStatsResponseItem[]
    os: GeneralStatsResponseItem[]
    device: GeneralStatsResponseItem[]
  }
  total: number
}
export type GeneralStatsAPIDataset = {
  browser: GeneralStats
  os: GeneralStats
  device: GeneralStats
}
export type GeneralStatsExtraArgs = { map?: string }

export type iGeneralStatsAPI = {
  dataset: ComputedRef<Readonly<GeneralStatsAPIDataset>>
  loading: ComputedRef<boolean>
  setRouter: (router: HttpRouter<'get'>) => iGeneralStatsAPI
  fetchForTeam: (args: FetchForTeamArgs<GeneralStatsExtraArgs>) => Promise<void>
  fetchForAlias: (args: FetchForAliasArgs<GeneralStatsExtraArgs>) => Promise<void>
  fetchForTag: (args: FetchForTagArgs<GeneralStatsExtraArgs>) => Promise<void>
}

/**
 * Returns data that can be used as a sensible default for testing and development
 */
export function DefaultGeneralStats(): GeneralStatsResponse {
  return {
    dataset: {
      browser: [
        {
          name: 'Safari',
          value: 19,
          children: [
            {
              name: '18',
              value: 18,
            },
            {
              name: '16',
              value: 1,
            },
          ],
        },
        {
          name: 'Chrome',
          value: 18,
          children: [
            {
              name: '140',
              value: 15,
            },
            {
              name: '139',
              value: 3,
            },
          ],
        },
        {
          name: 'WPS Office',
          value: 3,
          children: [
            {
              name: '17',
              value: 3,
            },
          ],
        },
        {
          name: 'WhatsApp',
          value: 2,
          children: [
            {
              name: '2',
              value: 2,
            },
          ],
        },
        {
          name: 'ReactorNetty',
          value: 1,
          children: [
            {
              name: '1',
              value: 1,
            },
          ],
        },
      ],
      os: [
        {
          name: 'Android',
          value: 19,
          children: [
            {
              name: 10,
              value: 16,
            },
            {
              name: 13,
              value: 3,
            },
          ],
        },
        {
          name: 'iOS',
          value: 18,
          children: [
            {
              name: 18,
              value: 18,
            },
          ],
        },
        {
          name: 'Mac',
          value: 3,
          children: [
            {
              name: 10,
              value: 3,
            },
          ],
        },
      ],
      device: [
        {
          name: 'Smartphone',
          value: 37,
        },
        {
          name: 'Desktop',
          value: 3,
        },
      ],
    },
    total: 43,
  }
}

/**
 * Fetches data for top os, browser, etc
 */
export default function GeneralStatsAPI(debug = false): iGeneralStatsAPI {
  const state = reactive<{
    dataset: GeneralStatsAPIDataset
    loading: boolean
  }>({
    dataset: {
      os: new GeneralStats(),
      browser: new GeneralStats(),
      device: new GeneralStats(),
    },
    loading: false,
  })

  let router: HttpRouter<'get'> = axios

  async function _fetch(url: string): Promise<void> {
    state.loading = true

    if (debug) {
      const fakeData = DefaultGeneralStats()
      state.dataset = {
        os: new GeneralStats(fakeData.dataset.os, fakeData.total),
        browser: new GeneralStats(fakeData.dataset.browser, fakeData.total),
        device: new GeneralStats(fakeData.dataset.device, fakeData.total),
      }
    } else {
      const response: HttpResponse<GeneralStatsResponse> =
        await router.get(url)

      if (response.status !== 200) {
        throw new Error('Could not fetch general stats data from ' + url)
      }

      const theData = response.data.dataset
      state.dataset = {
        os: new GeneralStats(theData.os, response.data.total),
        browser: new GeneralStats(theData.browser, response.data.total),
        device: new GeneralStats(theData.device, response.data.total),
      }
    }

    state.loading = false
  }

  return {
    loading: computed(() => state.loading),
    dataset: computed(() => state.dataset),

    fetchForTeam: async function ({
      orgID,
      teamID = null,
      map = 'world',
      from,
      to,
      archived = false,
    }): Promise<void> {
      const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/general?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`
      return _fetch(url)
    },

    fetchForAlias: async function ({
      orgID,
      teamID = null,
      alias,
      map = 'world',
      from,
      to,
      archived = false,
    }): Promise<void> {
      const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/general?alias[]=${encodeURIComponent(alias)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`
      return _fetch(url)
    },

    fetchForTag: async function ({
      orgID,
      teamID = null,
      tag,
      map = 'world',
      from,
      to,
      archived = false,
    }): Promise<void> {
      const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/general?tag[]=${encodeURIComponent(tag)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`
      return _fetch(url)
    },

    /**
     * Allows you to swap out the router for a fake one when writing tests
     */
    setRouter(newRouter) {
      router = newRouter
      return this
    },
  }
}
